import React, { useState, useEffect } from "react";
import "./Budget.css";
import { ArrowLeft, MoreVertical, ChevronDown, ChevronLeft, ChevronRight, Play, Pencil, Plus } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { getApi, deleteApi } from "../common/api-client";
import DeleteModal from "../common/DeleteModal";


const BudgetView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const [activeTab, setActiveTab] = useState("Employee Hours");
  const [budgetData, setBudgetData] = useState(null);
  const [budgetTickets, setBudgetTickets] = useState([]);
  const [ticketSummary, setTicketSummary] = useState(null);
  const [employeeHours, setEmployeeHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);


  // Pagination states for tickets
  const [ticketsLimit, setTicketsLimit] = useState(10);
  const [ticketsOffset, setTicketsOffset] = useState(0);
  const [ticketsPageCount, setTicketsPageCount] = useState(0);
  const [ticketsTotal, setTicketsTotal] = useState(0);

  // Pagination states for employee hours
  const [empLimit, setEmpLimit] = useState(10);
  const [empOffset, setEmpOffset] = useState(0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".more-actions-wrapper")) {
        setShowActionsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDelete = async () => {
    try {
      const response = await deleteApi(`budget/${id}`);
      if (response?.success === true) {
        navigate("/budget");
      } else {
        console.error(response?.message || "Error deleting budget");
      }
    } catch (error) {
      console.error("Error deleting budget:", error);
    } finally {
      setIsOpenDelete(false);
    }
  };


  const fetchBudgetDetails = async () => {
    if (!id) return;
    try {
      const response = await getApi(`budget/view/${id}`);
      if (response?.success) {
        setBudgetData(response.budget);
      }
    } catch (error) {
      console.error("Error fetching budget details:", error);
    }
  };

  const fetchEmployeeHours = async () => {
    if (!id) return;
    try {
      const response = await getApi(`budget/employee_hours/${id}`);
      if (response?.success) {
        setEmployeeHours(response.employee_hours || response.data?.employee_hours || []);
      }
    } catch (error) {
      console.error("Error fetching employee hours:", error);
    }
  };

  const fetchBudgetTickets = async () => {
    if (!id) return;
    try {
      const response = await getApi(`budget/tickets/${id}`, {
        limit: ticketsLimit,
        skip: ticketsOffset
      });
      if (response?.success) {
        setBudgetTickets(response.tickets || response.data?.tickets || []);
        setTicketSummary(response.summary || response.data?.summary || null);
        const total = response.total_rec || 0;
        setTicketsTotal(total);
        // Force calculation on frontend to be safe
        setTicketsPageCount(Math.ceil(total / ticketsLimit));
      }
    } catch (error) {
      console.error("Error fetching budget tickets:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBudgetDetails(), fetchEmployeeHours()]);
      setLoading(false);
    };
    loadData();
  }, [id]);

  useEffect(() => {
    fetchBudgetTickets();
  }, [id, ticketsLimit, ticketsOffset]);

  const users = [
    { id: 1, name: "Sakthivel", initial: "S" },
    { id: 2, name: "Senthil kumar", initial: "S" },
    { id: 3, name: "Nandhu", initial: "N" },
    { id: 4, name: "Gokula Krishnan", initial: "G" },
  ];

  const timeToDecimal = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return hours + (minutes / 60) + (seconds / 3600);
  };

  const chartDataRaw = budgetData?.chart_data || [];
  const maxHours = Math.max(...chartDataRaw.map(d => timeToDecimal(d.value)), 10);

  const chartData = chartDataRaw.map(item => ({
    day: item.label,
    hours: timeToDecimal(item.value),
    displayValue: `${item.value.split(':').slice(0, 2).join(':')} hrs`, // Show HH:MM hrs
    active: false
  }));

  // Merge budgetData.users with employeeHours to display all assigned users even without tickets/hours
  const getMergedEmployees = () => {
    const assignedUsers = budgetData?.users || [];
    const hoursEmployees = employeeHours || [];
    const merged = [];
    const processedHoursEmpIds = new Set();

    assignedUsers.forEach((user) => {
      const userId = user.id || user.value;
      const userName = user.name || user.label || user.full_name || user.user_name;

      // Find matching employee in employeeHours
      const match = hoursEmployees.find((emp) => {
        const empId = emp.id || emp.employee_id || emp.user_id || emp.value;
        if (userId && empId && String(userId) === String(empId)) return true;

        const empName = emp.name || emp.label || emp.full_name || emp.user_name;
        if (userName && empName && userName.trim().toLowerCase() === empName.trim().toLowerCase()) return true;

        return false;
      });

      if (match) {
        merged.push({
          ...user,
          ...match,
          name: userName || match.name,
          spent_hours: match.spent_hours || 0,
          ticket_count: match.ticket_count || match.tickets_count || 0,
        });
        processedHoursEmpIds.add(match.id || match.employee_id || match.user_id || match.value || match.name);
      } else {
        merged.push({
          ...user,
          name: userName,
          email: user.email || "-",
          spent_hours: 0,
          ticket_count: 0,
          period: budgetData?.period || "-",
        });
      }
    });

    // Also include any other employees in employeeHours who were not matched
    hoursEmployees.forEach((emp) => {
      const empId = emp.id || emp.employee_id || emp.user_id || emp.value || emp.name;
      if (!processedHoursEmpIds.has(empId)) {
        merged.push({
          ...emp,
          spent_hours: emp.spent_hours || 0,
          ticket_count: emp.ticket_count || emp.tickets_count || 0,
        });
      }
    });

    return merged;
  };

  const employees = getMergedEmployees();

  const filteredEmployees = employees.filter(emp => emp.name || emp.label || emp.full_name || emp.user_name || emp.id);
  const empPageCount = Math.ceil(filteredEmployees.length / empLimit);
  const displayedEmployees = filteredEmployees.slice(empOffset, empOffset + empLimit);

  const ticketsList = budgetTickets || [];

  const getTypeStyle = (type) => {
    return { backgroundColor: "#F3F4F6", color: "#6B7280", border: "1px solid rgba(107, 114, 128, 0.1)" };
  };

  if (loading) return <div className="p-4">Loading budget details...</div>;
  if (!budgetData) return <div className="p-4">No budget data found.</div>;

  const activeStatus = budgetData?.status || "Draft";

  const renderTypes = () => {
    const typesArray = budgetData?.types || (budgetData?.type ? [budgetData.type] : []);
    if (typesArray.length === 0) return <span className="badge type-badge">-</span>;
    
    return (
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {typesArray.map((t, i) => {
          const label = typeof t === 'object' ? t.label : t;
          return (
            <span key={i} className="badge type-badge" style={getTypeStyle(label)}>
              {label?.toUpperCase()}
            </span>
          );
        })}
      </div>
    );
  };

  const getStatusClassName = (status) => {
    return (status || "").toLowerCase().replace(/\s+/g, "_");
  };

  return (
    <div className="budget-view-container">
      <div className="view-header">
        <div className="view-header-left">
          <button className="back-btn" onClick={() => navigate("/budget")}>
            <ArrowLeft size={24} />
          </button>
          <h1>{budgetData?.budget_code || budgetData?.code}</h1>
        </div>
        <div className="view-header-right">
          <div className="status-tab-group">
            {activeStatus?.toLowerCase() === "draft" && (
              <button className="status-tab draft">
                Draft
              </button>
            )}
            {activeStatus?.toLowerCase() === "active" && (
              <button className="status-tab active">
                Active
              </button>
            )}
            {activeStatus?.toLowerCase() === "closed" && (
              <button className="status-tab closed">
                Closed
              </button>
            )}
          </div>
          <button className="edit-action-btn" onClick={() => navigate(`/budget/edit/${budgetData?.id}`, { state: { from: "view" } })}>
            Edit
          </button>

          <div className="more-actions-wrapper" style={{ position: "relative" }}>
            <button
              className="more-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowActionsDropdown(!showActionsDropdown);
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <MoreVertical size={20} style={{ pointerEvents: "none" }} />
            </button>
            {showActionsDropdown && (
              <div
                className="actions-dropdown-menu"
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: "8px",
                  backgroundColor: "white",
                  border: "1px solid #E5E5E5",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  zIndex: 100,
                  minWidth: "120px",
                  overflow: "hidden",
                }}
              >
                <div
                  onClick={() => {
                    setShowActionsDropdown(false);
                    const status = budgetData?.status?.toLowerCase();
                    if (["closed", "active"].includes(status)) {
                      return;
                    }
                    setIsOpenDelete(true);
                  }}
                  style={{
                    padding: "10px 16px",
                    cursor: ["closed", "active"].includes(budgetData?.status?.toLowerCase()) ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    color: ["closed", "active"].includes(budgetData?.status?.toLowerCase()) ? "#c0c0c0" : "#FF4444",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => {
                    if (!["closed", "active"].includes(budgetData?.status?.toLowerCase())) {
                      e.currentTarget.style.backgroundColor = "#fff1f1";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  Delete
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <div className="view-main-content">
        <div className="view-left-column">
          <div className="budget-info-card">
            <div className="id-badge">ID - {budgetData?.budget_code || budgetData?.code}</div>
            <div className="budget-profile">
              <div className="profile-avatar">BD</div>
              <div className="profile-text">
                <h2 title={budgetData?.budget_title || budgetData?.title}>{budgetData?.budget_title || budgetData?.title}</h2>
                <p>Created on {budgetData?.created_at_formatted || "-"}</p>
              </div>
            </div>

            <div className="details-section">
              <h3>Budget details</h3>
              <div className="details-grid">
                <div className="detail-row">
                  <span className="label">Project</span>
                  <span className="value"><strong>{budgetData?.project_name || budgetData?.project}</strong></span>
                </div>
                <div className="detail-row">
                  <span className="label">Type</span>
                  <span className="value">
                    {renderTypes()}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Pricing Type</span>
                  <div className="value pricing-value">
                    <span className="badge pricing-badge">{(budgetData?.pricing_type || "").replace('_', ' ').toUpperCase()}</span>
                  </div>
                </div>
                <div className="detail-row">
                  <span className="label">Period Type</span>
                  <span className="value">
                    <span className="badge period-badge">{(budgetData?.period_type || "").toUpperCase()}</span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Timing Type</span>
                  <span className="value">
                    <span className="badge timing-badge">{(budgetData?.timing_type || "").toUpperCase()}</span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Start Date</span>
                  <span className="value"><strong>{budgetData?.start_date_formatted || "-"}</strong></span>
                </div>
                <div className="detail-row">
                  <span className="label">End Date</span>
                  <span className="value"><strong>{budgetData?.end_date_formatted || "-"}</strong></span>
                </div>
                <div className="detail-row">
                  <span className="label">Description</span>
                  <span className="value description">
                    {budgetData?.description || "No description provided."}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="view-right-column">
          <div className="related-details-card">
            <h3>Related details</h3>
            <div className="period-progress-section">
              <div className="period-header">
                <span>PERIOD</span>
              </div>
              <div className="progress-bar-container">
                <div className="spent-label" style={{ left: `${Math.min(((parseFloat(budgetData?.spent_hours || ticketSummary?.logged_hours) || 0) / (parseFloat(budgetData?.duration) || 1)) * 100, 100)}%` }}>
                  <strong>{budgetData?.spent_hours || ticketSummary?.logged_hours || 0}hrs</strong> spent
                  <div className="spent-indicator"></div>
                </div>
                <div className="progress-bar-rail">
                  <div className="progress-fill" style={{ width: `${Math.min(((parseFloat(budgetData?.spent_hours || ticketSummary?.logged_hours) || 0) / (parseFloat(budgetData?.duration) || 1)) * 100, 100)}%` }}></div>
                  <div className="progress-thumb" style={{ left: `${Math.min(((parseFloat(budgetData?.spent_hours || ticketSummary?.logged_hours) || 0) / (parseFloat(budgetData?.duration) || 1)) * 100, 100)}%` }}></div>
                  <div className="progress-markers">
                    {[...Array(9)].map((_, i) => <div key={i} className="marker"></div>)}
                  </div>
                </div>
              </div>
              <div className="period-footer">
                <span>{budgetData?.start_date_formatted || "-"}</span>
                <div className="total-hrs-badge">Total hours : <strong>{budgetData?.duration || 0}hrs</strong></div>
                <span>{budgetData?.end_date_formatted || "-"}</span>
              </div>
            </div>

            <div className="cost-info-grid">
              <div className="cost-item purple">
                <div className="cost-row">
                  <span className="cost-label">Cost per hour</span>
                  <span className="cost-value">₹{budgetData?.cost_per_hour || 0}</span>
                </div>
                <div className="cost-row sub">
                  <span className="cost-label">Total cost</span>
                  <span className="cost-value">₹{budgetData?.total_cost || 0}</span>
                </div>
              </div>
              <div className="cost-item green">
                <div className="cost-row">
                  <span className="cost-label">Spent hrs Cost</span>
                  <span className="cost-value">₹{(parseFloat(budgetData?.spent_hours || ticketSummary?.logged_hours) || 0) * (parseFloat(budgetData?.cost_per_hour) || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="users-card">
            <h3>Users - {employees?.length || 0}</h3>
            <div className="users-list">
              {(employees || []).map((user, idx) => (
                <div key={idx} className="user-tag">
                  <div className="user-avatar">{user.name?.charAt(0) || user.label?.charAt(0) || "U"}</div>
                  <span>{user.name || user.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="hours-spent-section">
        <div className="section-header">
          <h3>Hours Spent <span>({budgetData?.start_date_formatted || "-"} - {budgetData?.end_date_formatted || "-"})</span></h3>
          <div className="pagination-arrows">
            <button className="arrow-btn"><ChevronLeft size={18} /></button>
            <button className="arrow-btn"><ChevronRight size={18} /></button>
          </div>
        </div>

        <div className="chart-container" style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {chartData.length > 0 && chartData.some(d => d.hours > 0) ? (
            chartData.map((data, i) => (
              <div key={i} className="chart-bar-group">
                <div 
                  className={`chart-bar ${data.active ? "active" : ""}`} 
                  style={{ height: `${(data.hours / maxHours) * 150}px` }}
                  title={`${data.displayValue} hrs`}
                >
                  <div className="bar-hover-value">{data.displayValue}</div>
                </div>
                <div className="chart-label">
                  <span className="day-num">{data.day.split(" ")[0]}</span>
                  <span className="month-name">{data.day.split(" ")[1]}</span>
                </div>
              </div>
            ))
          ) : (
            <div style={{ color: '#8790A1', fontSize: '14px' }}>No hours spent recorded for this period</div>
          )}
        </div>
      </div>

      <div className="employee-tickets-section">
        <div className="view-tabs">
          <button
            className={`view-tab ${activeTab === "Employee Hours" ? "active" : ""}`}
            onClick={() => setActiveTab("Employee Hours")}
          >
            Employee Hours
          </button>
          <button
            className={`view-tab ${activeTab === "Tickets" ? "active" : ""}`}
            onClick={() => setActiveTab("Tickets")}
          >
            Tickets
          </button>
        </div>

        {activeTab === "Tickets" && (
          <div className="tickets-summary-cards">
            <div className="summary-card purple">
              <span className="summary-label">Total Hours</span>
              <div className="summary-value">{ticketSummary?.total_hours || 0} <span>hrs</span></div>
            </div>
            <div className="summary-card yellow">
              <span className="summary-label">Estimation Hours</span>
              <div className="summary-value">{ticketSummary?.estimation_hours || 0} <span>hrs</span></div>
            </div>
            <div className="summary-card blue">
              <span className="summary-label">Logged Hours</span>
              <div className="summary-value">{ticketSummary?.logged_hours || 0} <span>hrs</span></div>
            </div>
            <div className="summary-card green">
              <span className="summary-label">Manual Hours</span>
              <div className="summary-value">{ticketSummary?.manual_hours || 0} <span>hrs</span></div>
            </div>
          </div>
        )}

        <div className="tab-content-table">
          {activeTab === "Employee Hours" ? (
            <div style={{ overflowX: "auto" }}>
              <table className="budget-table view-page-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Budget</th>
                    <th className="text-center">Spent Hours</th>
                    <th className="text-center">Tickets count</th>
                    <th>Period</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedEmployees.length > 0 ? (
                    displayedEmployees.map((emp, i) => {
                      const empName = emp.name || emp.label || emp.full_name || emp.user_name;
                      return (
                        <tr key={i}>
                          <td>
                            <div className="employee-cell">
                              <div className="emp-avatar">{empName?.charAt(0) || "?"}</div>
                              <span>{empName || "N/A"}</span>
                            </div>
                          </td>
                          <td className="muted-column">{emp.email || "-"}</td>
                          <td className="muted-column text-center">{emp.spent_hours || 0}</td>
                          <td className="text-center">
                            <span className="badge ticket-badge lowercase">{emp.ticket_count || emp.tickets_count || 0} tickets</span>
                          </td>
                          <td className="muted-column">{emp.period || budgetData?.period || "-"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '60px', color: '#8790A1' }}>
                        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>No employee data available</div>
                        <div style={{ fontSize: '13px' }}>There are no recorded hours for this project budget yet.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="budget-table tickets-table view-page-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Task Title</th>
                    <th>
                      Project 
                    </th>
                    <th>
                      Module 
                    </th>
                    <th>Assigned to</th>
                    <th>Reporter</th>
                    <th>Expected date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {ticketsList.length > 0 && ticketsList.some(t => t.id || t.code || t.task_title) ? (
                    ticketsList.filter(t => t.id || t.code || t.task_title).map((ticket, i) => (
                      <tr key={i}>
                        <td>{ticket.code || ticket.ticket_code}</td>
                        <td>
                          <div className="task-title-cell">
                            {ticket.task_title || ticket.title}
                          </div>
                        </td>
                        <td>{ticket.project || ticket.project_name}</td>
                        <td>
                          <span className="badge type-badge" style={getTypeStyle(ticket.module || ticket.module_name)}>
                            {(ticket.module || ticket.module_name || "-").toUpperCase()}
                          </span>
                        </td>
                        <td>{ticket.assigned_to || ticket.assigned_to_name || ticket.assignedTo}</td>
                        <td>{ticket.reporter || ticket.reporter_name}</td>
                        <td>{ticket.expected_date || ticket.expectedDate}</td>
                        <td>
                          <div className="status-indicator">
                            <span className={`dot ${getStatusClassName(ticket.status)}`}></span>
                            <span className={`status-text ${getStatusClassName(ticket.status)}`}>{ticket.status}</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '60px', color: '#8790A1' }}>
                        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>No tickets found</div>
                        <div style={{ fontSize: '13px' }}>There are no tickets linked to this project budget.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {activeTab === "Employee Hours" && filteredEmployees.length > 0 && (
          <div className="pagination-footer">
            <div className="records-per-page">
              Records per page :
              <div className="records-select-wrapper">
                <select 
                  className="records-select"
                  value={empLimit}
                  onChange={(e) => {
                    setEmpLimit(Number(e.target.value));
                    setEmpOffset(0);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <ChevronDown className="select-icon" size={14} />
              </div>
            </div>
            <div className="pagination-controls">
              <ReactPaginate
                previousLabel={<ChevronLeft size={18} />}
                nextLabel={<ChevronRight size={18} />}
                breakLabel={"..."}
                pageCount={empPageCount}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={(event) => setEmpOffset(event.selected * empLimit)}
                containerClassName={"pagination"}
                activeClassName={"active"}
                forcePage={empPageCount > 0 ? Math.floor(empOffset / empLimit) : 0}
              />
            </div>
          </div>
        )}

        {activeTab === "Tickets" && ticketsList.length > 0 && (
          <div className="pagination-footer">
            <div className="records-per-page">
              Records per page :
              <div className="records-select-wrapper">
                <select 
                  className="records-select"
                  value={ticketsLimit}
                  onChange={(e) => {
                    setTicketsLimit(Number(e.target.value));
                    setTicketsOffset(0);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <ChevronDown className="select-icon" size={14} />
              </div>
            </div>
            <div className="pagination-controls">
              <ReactPaginate
                previousLabel={<ChevronLeft size={18} />}
                nextLabel={<ChevronRight size={18} />}
                breakLabel={"..."}
                pageCount={ticketsPageCount}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={(event) => setTicketsOffset(event.selected * ticketsLimit)}
                containerClassName={"pagination"}
                activeClassName={"active"}
                forcePage={ticketsPageCount > 0 ? Math.floor(ticketsOffset / ticketsLimit) : 0}
              />
            </div>
          </div>
        )}
      </div>
      <DeleteModal
        isOpen={isOpenDelete}
        onClose={() => setIsOpenDelete(false)}
        onDelete={handleDelete}
        title="Delete Budget"
        message="Are you sure you want to delete this budget?"
      />
    </div>
  );
};


export default BudgetView;
